import React, { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { InvitationQueueMember, Criterion } from 'queries';
import moment from 'moment-timezone';
import { Chip } from '@app/core';
import { RawCheckbox } from '@app/form';

const useStyles = createUseStyles((theme: Theme) => ({
  table: {
    width: '100%',
    backgroundColor: theme.colors.white,
    borderCollapse: 'collapse',
    '& td, & th': {
      padding: theme.marginBase,
      borderBottom: {
        width: 1,
        style: 'solid',
        color: theme.colors.grayBorder,
      },
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  rank: {
    ...theme.fonts.h5,
  },
  score: {
    ...theme.fonts.h5,
    color: theme.colors.greenAccessible,
  },
  chips: {
    padding: theme.marginBase,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    '& > *': {
      marginRight: theme.marginBase,
    },
  },
}));

const getValue = (ref: string, participant?: any) => {
  if (!participant) {
    return '';
  }
  if (ref === 'birthdate') {
    const date = participant[ref];
    if (!date || !moment(date).isValid()) {
      return '';
    }
    return moment().diff(date, 'years') + ' ans';
  }

  const val = participant[ref];

  if (!val) {
    return '';
  }

  if (Array.isArray(val)) {
    return val.join(', ');
  }

  if (moment(val).isValid()) {
    if (ref.startsWith('last')) {
      return moment(val).fromNow();
    }
    return moment(val).format('LL');
  }

  return val;
};

interface Props {
  members: InvitationQueueMember[];
  handledFields?: string[];
  hideRank?: boolean;
  onSelect?: (id: string) => void;
  selected?: string[];
}

export const QueueMembersTable = ({
  handledFields = [],
  members,
  hideRank,
  onSelect,
  selected,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const hasScore = handledFields.includes('score');
  const fields = useMemo(
    () =>
      handledFields.filter(
        (f) =>
          ![
            'score',
            'studySubjectPreferences',
            'studyTypePreferences',
          ].includes(f),
      ),
    [handledFields],
  );

  return (
    <table className={classes.table}>
      <tr>
        {!!onSelect && <th />}
        {!hideRank && (
          <>
            <th>Rank</th>
            <th>Score</th>
          </>
        )}
        <th>Prénom</th>
        {hasScore && (
          <>
            <th>Notes</th>
            <th>Activité</th>
          </>
        )}
        {fields.map((field) => (
          <th>{field}</th>
        ))}
      </tr>
      {members.map((member, rank) => (
        <tr key={member.id || rank}>
          {!!onSelect && (
            <td>
              <RawCheckbox
                checked={selected?.includes(member.id)}
                onChange={() => onSelect(member.id)}
              />
            </td>
          )}
          {!hideRank && (
            <>
              <td className={classes.rank}>#{rank + 1}</td>
              <td className={classes.score}>
                {Math.round(member.searchScore * 1000) / 10}%
              </td>
            </>
          )}
          <td>{member?.firstName}</td>
          {hasScore && (
            <>
              <td className={classes.score}>
                {member?.evaluationScore
                  ? `${Math.round(member.evaluationScore * 1000) / 10}%`
                  : undefined}
              </td>
              <td className={classes.score}>
                {member.activityScore
                  ? `${Math.round(member.activityScore * 1000) / 10}%`
                  : undefined}
              </td>
            </>
          )}
          {fields.map((field) => (
            <td>{getValue(field, member) || '/'}</td>
          ))}
        </tr>
      ))}
    </table>
  );
};
